import { PositionContext, ScrollTimeline } from "../../displayComponents/displayElements/DisplayElements";
import { RefObject, useContext, useEffect, useRef, useState } from "react";

import { NavigationType } from "../../state/navigation";
import gsap from "gsap";
import { useRecoilValue } from "recoil";

function useTappedAnimation({ elRef, tapped, atomId }: { elRef: RefObject<HTMLDivElement>, tapped: boolean, atomId: number }) {
    const [renderedTapped] = useState(tapped);
    const navigationType = useRecoilValue(NavigationType);
    const scrollTimeline = useContext(ScrollTimeline);
    const position = useContext(PositionContext);
    
    // Store animation state to prevent recreation
    const animationRef = useRef<gsap.core.Tween | null>(null);
    const hasInitialized = useRef(false);
    
    // Initialize scroll animation once
    useEffect(() => {
        if (!elRef.current || hasInitialized.current) return;
        if (!renderedTapped && navigationType === "scroll" && position === "top") {
            // Set initial state once
            gsap.set(elRef.current, { autoAlpha: 0, x: "200%" });
            
            // Create animation once and store it
            const tween = gsap.to(elRef.current, { 
                x: 0, 
                autoAlpha: 1, 
                duration: 40, 
                stagger: atomId,
                paused: true // Start paused
            });
            
            animationRef.current = tween;
            scrollTimeline?.add(tween, atomId * 3);
            hasInitialized.current = true;
        }
        
        return () => {
            if (animationRef.current) {
                animationRef.current.kill();
                animationRef.current = null;
            }
        };
    }, [navigationType, position]); // Only recreate if navigation type or position changes
    
    // Handle tap animations with minimal re-creation
    useEffect(() => {
        if (!elRef.current || renderedTapped) return;
        if (navigationType !== "tap") return;
        
        // Set initial state for tap
        gsap.set(elRef.current, { autoAlpha: 0 });
        
        // Only animate if tapped
        if (tapped) {
            gsap.to(elRef.current, { autoAlpha: 1 });
        }
    }, [tapped, navigationType]); // Only essential dependencies
}


export default useTappedAnimation